import { useEffect, useState } from 'react'
import { adminAPI } from '../services/api'
import { apiErrorMessage } from '../utils/apiError'
import type { OutcomeAuditGroup, OutcomeAuditReport, OutcomeIssue, OutcomeTally } from '../types/outcomeAudit'

/**
 * Admin diagnostics: closed signals and logged trades whose stored outcome or
 * P&L disagrees with their own entry and exit prices (backend
 * services/outcome_audit). Mostly rows closed before outcomes were scored by
 * P&L (2026-09-14), which were never re-scored. Read-only: nothing here
 * re-labels a row. Rendered as the server sends it.
 */

const GROUPS: Record<OutcomeAuditGroup['group'], string> = {
  engine: 'Engine signals',
  creator_calls: "Creators' calls",
  logged_trades: 'Logged trades',
}

const ISSUES: Record<OutcomeIssue, string> = {
  label_contradicts_pnl: 'Outcome contradicts P&L',
  pnl_mismatch: 'Stored P&L differs',
  unpriced: 'No exit price',
}

const pct = (v: number | null) => (v === null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`)

export const tally = (t: OutcomeTally) =>
  `${t.wins}W / ${t.losses}L / ${t.breakevens}BE · ${t.win_rate === null ? '—' : `${t.win_rate.toFixed(1)}%`}`

const Group = ({ g }: { g: OutcomeAuditGroup }) => (
  <div className="border-t border-gray-100 p-4" data-testid={`audit-${g.group}`}>
    <h3 className="text-sm font-semibold text-gray-800">
      {GROUPS[g.group]} · {g.examined} closed · {g.flagged} flagged
    </h3>
    {g.flagged > 0 && (
      <p className="mt-1 text-xs text-gray-500">
        {(Object.keys(ISSUES) as OutcomeIssue[])
          .filter((k) => g.counts[k] > 0)
          .map((k) => `${ISSUES[k]} ${g.counts[k]}`)
          .join(' · ')}
      </p>
    )}
    {g.rows.length > 0 && (
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500">
              <th className="px-2 py-1 text-left font-medium">#</th>
              <th className="px-2 py-1 text-left font-medium">Signal</th>
              <th className="px-2 py-1 text-left font-medium">Stored</th>
              <th className="px-2 py-1 text-left font-medium">Prices give</th>
              <th className="px-2 py-1 text-left font-medium">Issue</th>
            </tr>
          </thead>
          <tbody>
            {g.rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100" data-testid={`audit-row-${g.group}-${r.id}`}>
                <td className="px-2 py-1.5 tabular-nums text-gray-500">{r.id}</td>
                <td className="px-2 py-1.5 text-gray-800">
                  {r.symbol} {r.direction}
                  {r.in_record === false && <span className="ml-1 text-xs text-gray-400">(not in record)</span>}
                </td>
                <td className="px-2 py-1.5 tabular-nums">
                  {r.stored_outcome} {pct(r.stored_pnl_pct)}
                </td>
                <td className="px-2 py-1.5 tabular-nums">
                  {r.priced_outcome ?? '—'} {pct(r.priced_pnl_pct)}
                </td>
                <td className="px-2 py-1.5 text-xs text-amber-700">{r.issues.map((i) => ISSUES[i]).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {g.flagged > g.rows.length && (
          <p className="mt-1 text-xs text-gray-500">… and {g.flagged - g.rows.length} more</p>
        )}
      </div>
    )}
  </div>
)

const OutcomeAuditPanel = () => {
  const [data, setData] = useState<OutcomeAuditReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    adminAPI
      .outcomeAudit()
      .then((d) => live && setData(d))
      .catch((e) => live && setError(apiErrorMessage(e, 'Could not load the outcome audit')))
    return () => {
      live = false
    }
  }, [])

  return (
    <section className="rounded-lg border border-gray-200 bg-white" aria-label="Outcome audit" data-testid="outcome-audit">
      <div className="p-4">
        <h2 className="text-lg font-semibold text-gray-900">Outcome audit (read-only)</h2>
        <p className="mt-1 text-sm text-gray-600">
          Closed rows whose stored win, loss or P&amp;L disagrees with their own entry and exit prices. Nothing here
          changes a row.
        </p>
        {data && (
          <p className="mt-2 text-sm text-gray-800" data-testid="audit-record">
            Engine record as stored: {tally(data.record.stored)}
            {data.record.relabelled > 0 ? (
              <>
                {' '}
                → corrected: {tally(data.record.corrected)} ({data.record.relabelled} relabelled)
              </>
            ) : (
              ' · every outcome agrees with its prices'
            )}
          </p>
        )}
      </div>
      {error && <p className="px-4 pb-4 text-sm text-red-600">{error}</p>}
      {data?.groups.map((g) => <Group key={g.group} g={g} />)}
    </section>
  )
}

export default OutcomeAuditPanel
